import './Radar.css';

interface RadarValues {
  tech: number;   // 0–100
  phys: number;
  psych: number;
  social: number;
}

interface RadarProps {
  values: RadarValues;
  size?: number;
  className?: string;
}

// Corner order is fixed: Tech → Phys → Psych → Social. Do not reorder.
const AXES = [
  { key: 'tech',   angle: -90, color: 'var(--c-tech)' },
  { key: 'phys',   angle:   0, color: 'var(--c-phys)' },
  { key: 'psych',  angle:  90, color: 'var(--c-psych)' },
  { key: 'social', angle: 180, color: 'var(--c-social)' },
] as const;

function pointAt(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)] as const;
}

export function Radar({ values, size = 140, className = '' }: RadarProps) {
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 8;

  const points = AXES.map((a) => {
    const v = Math.max(0, Math.min(100, values[a.key])) / 100;
    return pointAt(cx, cy, maxR * v, a.angle);
  });
  const polygon = points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');

  return (
    <svg
      className={`radar ${className}`.trim()}
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label={`Radar — tech ${values.tech}, phys ${values.phys}, psych ${values.psych}, social ${values.social}`}
    >
      {[0.33, 0.66, 1].map((scale, i) => (
        <polygon
          key={i}
          points={AXES.map((a) => {
            const [x, y] = pointAt(cx, cy, maxR * scale, a.angle);
            return `${x.toFixed(2)},${y.toFixed(2)}`;
          }).join(' ')}
          fill="none"
          stroke="var(--line-1)"
          strokeWidth={i === 2 ? 1 : 0.5}
        />
      ))}
      {AXES.map((a) => {
        const [x, y] = pointAt(cx, cy, maxR, a.angle);
        return <line key={a.key} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line-1)" strokeWidth={0.5} />;
      })}
      <polygon
        points={polygon}
        fill="oklch(0.78 0.18 158 / 0.18)"
        stroke="var(--accent)"
        strokeWidth={1.4}
      />
      {AXES.map((a, i) => {
        const [x, y] = points[i];
        return <circle key={a.key} cx={x} cy={y} r={2.5} fill={a.color} />;
      })}
    </svg>
  );
}
